// Copyright (c) 2025 Goffy. All Rights Reserved.

import {
  ActivityIndicator,
  ImageBackground,
  type ImageSourcePropType,
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useUser } from '@/app/user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Text, View } from '@/components/Themed';
import { t } from '@/i18n';
import { getGameDisplayName, getSubgameDisplayName } from '@/features/game-definitions/i18n';
import {
  BestBallSelectImage,
  BunkerSelectImage,
  HighLowSelectImage,
  HoleIllustration,
  MysterySelectImage,
  PenaltyIcon,
  SkinsSelectImage,
  StablefordSelectImage,
  ThreePuttIcon,
  TrophyOnlyImage,
  WaterSelectImage,
  WorstBallSelectImage,
} from '@/features/hole/assets';

type Player = { id: string; name: string };
type GameScore = {
  gameId?: string;
  GameId?: string;
  gameName?: string;
  GameName?: string;
  playerId?: string;
  PlayerId?: string;
  scorePoints?: number;
  ScorePoints?: number;
};
type RoundScoreboardResponse = {
  roundId?: string;
  RoundId?: string;
  scores?: GameScore[];
  Scores?: GameScore[];
};
type HoleEntry = {
  playerId?: string;
  PlayerId?: string;
  hole?: number;
  Hole?: number;
  holeNumber?: number;
  HoleNumber?: number;
  par?: number;
  Par?: number;
  grossStroke?: number | null;
  GrossStroke?: number | null;
  grossStrokes?: number | null;
  GrossStrokes?: number | null;
  pickup?: boolean;
  Pickup?: boolean;
};
type RoundSubgameEntry = {
  playerId?: string;
  PlayerId?: string;
  holeNumber?: number;
  HoleNumber?: number;
  subgameId?: string;
  SubgameId?: string;
};
type HoleEventEntry = {
  playerId?: string;
  PlayerId?: string;
  holeNumber?: number;
  HoleNumber?: number;
  eventType?: string;
  EventType?: string;
  eventValue?: string | null;
  EventValue?: string | null;
};
type GameDefinition = {
  id?: string;
  Id?: string;
  name?: string;
  Name?: string;
};
type Round = {
  id: string;
  name?: string;
  courseName?: string;
  status?: string;
  createdAt: string;
  players: string[];
};

type GameResultRow = {
  playerId: string;
  playerName: string;
  points: number;
};

type GameResultCard = {
  gameId: string;
  gameName: string;
  rows: GameResultRow[];
};

type MysteryHighlight = {
  holeNumber: number;
  mysteryPrize: string;
  subgameName: string;
  winnerName: string;
};

type ParOrBetterRow = {
  playerName: string;
  holes: Array<{ holeNumber: number; resultLabel: string; resultType: 'par' | 'birdie' | 'eagle' | 'albatross' | 'underPar' }>;
};

type HazardByPlayerRow = {
  playerId: string;
  playerName: string;
  total: number;
  water: number;
  bunker: number;
  threePutt: number;
  penalty: number;
};

const GAME_ICON_MAP: Record<string, ImageSourcePropType> = {
  'best-ball': BestBallSelectImage,
  bunker: BunkerSelectImage,
  'high-low': HighLowSelectImage,
  mystery: MysterySelectImage,
  skins: SkinsSelectImage,
  stableford: StablefordSelectImage,
  water: WaterSelectImage,
  'worst-ball': WorstBallSelectImage,
};

const normalizeGameKey = (value: string | undefined): string => {
  if (!value) return '';
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

type HazardType = 'water' | 'bunker' | 'threePutt' | 'penalty';

const HAZARD_ICON_MAP: Record<HazardType, ImageSourcePropType> = {
  water: WaterSelectImage,
  bunker: BunkerSelectImage,
  threePutt: ThreePuttIcon,
  penalty: PenaltyIcon,
};

const normalizeHazardType = (value: string): HazardType | null => {
  const normalized = value.replace(/[\s_-]+/g, '').toLowerCase();
  if (normalized === 'water') return 'water';
  if (normalized === 'bunker') return 'bunker';
  if (normalized === 'threeputt') return 'threePutt';
  if (normalized === 'penalty') return 'penalty';
  return null;
};

const getString = (...values: Array<unknown>): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }
  return undefined;
};

const getNumber = (...values: Array<unknown>): number | undefined => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }
  return undefined;
};

const getBoolean = (...values: Array<unknown>): boolean | undefined => {
  for (const value of values) {
    if (typeof value === 'boolean') {
      return value;
    }
  }
  return undefined;
};

const parseEventValueObject = (value: string | null | undefined): Record<string, unknown> | null => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return null;
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (parsed && typeof parsed === 'object') {
      return parsed as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
};

const getParResult = (
  gross: number,
  par: number
): { resultLabel: string; resultType: 'par' | 'birdie' | 'eagle' | 'albatross' | 'underPar' } => {
  const delta = gross - par;
  if (delta === 0) return { resultLabel: t('summary.resultPar'), resultType: 'par' };
  if (delta === -1) return { resultLabel: t('summary.resultBirdie'), resultType: 'birdie' };
  if (delta === -2) return { resultLabel: t('summary.resultEagle'), resultType: 'eagle' };
  if (delta <= -3) return { resultLabel: t('summary.resultAlbatross'), resultType: 'albatross' };
  return { resultLabel: t('summary.resultUnderPar'), resultType: 'underPar' };
};

export default function RoundSummaryScreen() {
  const { roundId } = useLocalSearchParams<{ roundId?: string }>();
  const { user } = useUser();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const router = useRouter();
  const [round, setRound] = useState<Round | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [scores, setScores] = useState<GameScore[]>([]);
  const [holeEntries, setHoleEntries] = useState<HoleEntry[]>([]);
  const [roundSubgames, setRoundSubgames] = useState<RoundSubgameEntry[]>([]);
  const [holeEvents, setHoleEvents] = useState<HoleEventEntry[]>([]);
  const [gameDefinitions, setGameDefinitions] = useState<GameDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRoundData = useCallback(async () => {
    if (!roundId || !user?.token) {
      setLoading(false);
      setError(t('summary.missingInfo'));
      return;
    }

    try {
      setLoading(true);
      const [roundRes, playersRes, scoreboardRes, holeEntriesRes, subgamesRes, holeEventsRes, gameDefsRes] = await Promise.all([
        fetch(`${apiBaseUrl}/api/rounds/${roundId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        }),
        fetch(`${apiBaseUrl}/api/rounds/${roundId}/players`, {
          headers: { Authorization: `Bearer ${user.token}` },
        }),
        fetch(`${apiBaseUrl}/api/rounds/${roundId}/scoreboard`, {
          headers: { Authorization: `Bearer ${user.token}` },
        }),
        fetch(`${apiBaseUrl}/api/rounds/${roundId}/hole-entries`, {
          headers: { Authorization: `Bearer ${user.token}` },
        }),
        fetch(`${apiBaseUrl}/api/rounds/${roundId}/subgames`, {
          headers: { Authorization: `Bearer ${user.token}` },
        }),
        fetch(`${apiBaseUrl}/api/rounds/${roundId}/hole-events`, {
          headers: { Authorization: `Bearer ${user.token}` },
        }),
        fetch(`${apiBaseUrl}/api/game-definitions`, {
          headers: { Authorization: `Bearer ${user.token}` },
        }),
      ]);

      if (!roundRes.ok || !playersRes.ok || !scoreboardRes.ok) {
        throw new Error(t('summary.loadError'));
      }

      const roundData = await roundRes.json();
      const playersData = await playersRes.json();
      const scoreboardData: RoundScoreboardResponse = await scoreboardRes.json();
      const holeEntriesData = holeEntriesRes.ok ? await holeEntriesRes.json() : [];
      const subgamesData = subgamesRes.ok ? await subgamesRes.json() : [];
      const holeEventsData = holeEventsRes.ok ? await holeEventsRes.json() : [];
      const gameDefsData = gameDefsRes.ok ? await gameDefsRes.json() : [];

      const normalizedPlayers = (Array.isArray(playersData) ? playersData : (playersData.players ?? playersData.Players ?? []));
      const normalizedScores = Array.isArray(scoreboardData.scores)
        ? scoreboardData.scores
        : Array.isArray(scoreboardData.Scores)
          ? scoreboardData.Scores
          : [];

      setRound(roundData);
      setPlayers(normalizedPlayers);
      setScores(normalizedScores);
      setHoleEntries(Array.isArray(holeEntriesData) ? holeEntriesData : []);
      setRoundSubgames(Array.isArray(subgamesData) ? subgamesData : []);
      setHoleEvents(Array.isArray(holeEventsData) ? holeEventsData : []);
      setGameDefinitions(Array.isArray(gameDefsData) ? gameDefsData : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('summary.loadError'));
    } finally {
      setLoading(false);
    }
  }, [roundId, user?.token, apiBaseUrl]);

  useEffect(() => {
    fetchRoundData();
  }, [fetchRoundData]);

  const playerById = useMemo(() => {
    const map = new Map<string, Player>();
    players.forEach((player) => map.set(player.id, player));
    return map;
  }, [players]);

  const gameDefinitionNameById = useMemo(() => {
    const map = new Map<string, string>();
    gameDefinitions.forEach((definition) => {
      const id = getString(definition.id, definition.Id);
      const name = getGameDisplayName(
        {
          id,
          name: getString(definition.name, definition.Name),
          info: getString(definition.info, definition.Info),
          description: getString(definition.description, definition.Description),
        },
        t('summary.unknownGame')
      );
      if (id && name) {
        map.set(id.toLowerCase(), name);
      }
    });
    return map;
  }, [gameDefinitions]);

  const playerScores = useMemo(() => {
    const scoresByPlayer: Record<string, { total: number; games: Record<string, number> }> = {};
    players.forEach((player) => {
      scoresByPlayer[player.id] = { total: 0, games: {} };
    });

    scores.forEach((score) => {
      const playerId = getString(score.playerId, score.PlayerId);
      const gameName = getString(score.gameName, score.GameName);
      const points = getNumber(score.scorePoints, score.ScorePoints) ?? 0;
      if (playerId && scoresByPlayer[playerId]) {
        scoresByPlayer[playerId].total += points;
        if (gameName) {
          scoresByPlayer[playerId].games[gameName] = points;
        }
      }
    });

    return players
      .map((player) => ({
        ...player,
        totalPoints: scoresByPlayer[player.id]?.total ?? 0,
        games: scoresByPlayer[player.id]?.games ?? {},
      }))
      .sort((a, b) => b.totalPoints - a.totalPoints);
  }, [players, scores]);

  const gameResultCards = useMemo(() => {
    const byGame = new Map<string, { gameId: string; gameName: string; playerPoints: Map<string, number> }>();
    scores.forEach((score) => {
      const rawGameId = getString(score.gameId, score.GameId) ?? '';
      const gameName =
        getString(
          score.gameName,
          score.GameName,
          rawGameId ? gameDefinitionNameById.get(rawGameId.toLowerCase()) : undefined
        ) ?? t('summary.unknownGame');
      const key = rawGameId || gameName;
      if (!key) return;
      if (!byGame.has(key)) {
        byGame.set(key, {
          gameId: rawGameId || key,
          gameName,
          playerPoints: new Map<string, number>(),
        });
      }
      const entry = byGame.get(key);
      if (!entry) return;
      if (entry.gameName === t('summary.unknownGame') && gameName !== t('summary.unknownGame')) {
        entry.gameName = gameName;
      }
      const playerId = getString(score.playerId, score.PlayerId);
      const points = getNumber(score.scorePoints, score.ScorePoints) ?? 0;
      if (!playerId) return;
      entry.playerPoints.set(playerId, (entry.playerPoints.get(playerId) ?? 0) + points);
    });

    return Array.from(byGame.values())
      .map((game): GameResultCard => {
        const rows = Array.from(game.playerPoints.entries())
          .map(([playerId, points]) => ({
            playerId,
            playerName: playerById.get(playerId)?.name ?? t('summary.unknownPlayer'),
            points,
          }))
          .sort((a, b) => b.points - a.points);
        return {
          gameId: game.gameId,
          gameName: game.gameName,
          rows,
        };
      })
      .sort((a, b) => a.gameName.localeCompare(b.gameName));
  }, [scores, playerById, gameDefinitionNameById]);

  const mysteryHighlights = useMemo(() => {
    const winnerByKey = new Map<string, string>();
    holeEvents.forEach((event) => {
      const eventType = getString(event.eventType, event.EventType)?.toLowerCase() ?? '';
      if (!eventType.includes('mystery')) return;
      const parsedValue = parseEventValueObject(getString(event.eventValue, event.EventValue));
      if (!parsedValue) return;
      const subgameId = getString(
        parsedValue['subgameId'],
        parsedValue['SubgameId'],
        parsedValue['gameId'],
        parsedValue['GameId']
      );
      const winnerPlayerId = getString(
        parsedValue['winnerPlayerId'],
        parsedValue['WinnerPlayerId'],
        parsedValue['playerId'],
        parsedValue['PlayerId']
      );
      const holeNumber =
        getNumber(parsedValue['holeNumber'], parsedValue['HoleNumber'], event.holeNumber, event.HoleNumber) ?? 0;
      if (!subgameId || !winnerPlayerId || holeNumber <= 0) return;
      winnerByKey.set(`${holeNumber}:${subgameId}`, winnerPlayerId);
    });

    return roundSubgames
      .map((entry): MysteryHighlight | null => {
        const playerId = getString(entry.playerId, entry.PlayerId);
        const holeNumber = getNumber(entry.holeNumber, entry.HoleNumber) ?? 0;
        const subgameId = getString(entry.subgameId, entry.SubgameId);
        if (!playerId || !subgameId || holeNumber <= 0) return null;
        const key = `${holeNumber}:${subgameId}`;
        const winnerPlayerId = winnerByKey.get(key) ?? playerId;
        const winnerName = playerById.get(winnerPlayerId)?.name ?? t('summary.unknownWinner');
        const subgameName = getSubgameDisplayName(
          {
            id: subgameId,
            name: gameDefinitionNameById.get(subgameId.toLowerCase()),
          },
          t('summary.mysteryPrizeFallback')
        );
        const mysteryPrize = subgameName;
        return {
          holeNumber,
          mysteryPrize,
          subgameName,
          winnerName,
        };
      })
      .filter((entry): entry is MysteryHighlight => entry !== null)
      .sort((a, b) => a.holeNumber - b.holeNumber || a.mysteryPrize.localeCompare(b.mysteryPrize));
  }, [roundSubgames, holeEvents, playerById, gameDefinitionNameById]);

  const parOrBetterByPlayer = useMemo(() => {
    const byPlayer = new Map<string, Array<{ holeNumber: number; resultLabel: string }>>();
    holeEntries.forEach((entry) => {
      const playerId = getString(entry.playerId, entry.PlayerId);
      const holeNumber = getNumber(entry.holeNumber, entry.HoleNumber, entry.hole, entry.Hole) ?? 0;
      const par = getNumber(entry.par, entry.Par);
      const gross = getNumber(entry.grossStroke, entry.GrossStroke, entry.grossStrokes, entry.GrossStrokes);
      const pickup = getBoolean(entry.pickup, entry.Pickup) ?? false;
      if (!playerId || holeNumber <= 0 || par == null || gross == null || pickup) return;
      if (gross <= par) {
        const current = byPlayer.get(playerId) ?? [];
        const result = getParResult(gross, par);
        current.push({
          holeNumber,
          resultLabel: result.resultLabel,
          resultType: result.resultType,
        });
        byPlayer.set(playerId, current);
      }
    });

    return Array.from(byPlayer.entries())
      .map(([playerId, holes]): ParOrBetterRow => ({
        playerName: playerById.get(playerId)?.name ?? t('summary.unknownPlayer'),
        holes: holes.sort((a, b) => a.holeNumber - b.holeNumber),
      }))
      .sort((a, b) => b.holes.length - a.holes.length || a.playerName.localeCompare(b.playerName));
  }, [holeEntries, playerById]);

  const hazardByPlayer = useMemo(() => {
    const byPlayer = new Map<string, HazardByPlayerRow>();

    const getEventPlayerId = (event: HoleEventEntry): string | undefined => {
      const directPlayerId = getString(event.playerId, event.PlayerId);
      if (directPlayerId) return directPlayerId;
      const parsedValue = parseEventValueObject(getString(event.eventValue, event.EventValue));
      return getString(
        parsedValue?.['playerId'],
        parsedValue?.['PlayerId'],
        parsedValue?.['winnerPlayerId'],
        parsedValue?.['WinnerPlayerId']
      );
    };

    holeEvents.forEach((event) => {
      const playerId = getEventPlayerId(event);
      if (!playerId) return;
      const eventType = getString(event.eventType, event.EventType);
      if (!eventType) return;
      const hazardType = normalizeHazardType(eventType);
      if (!hazardType) return;

      const current =
        byPlayer.get(playerId) ??
        {
          playerId,
          playerName: playerById.get(playerId)?.name ?? t('summary.unknownPlayer'),
          total: 0,
          water: 0,
          bunker: 0,
          threePutt: 0,
          penalty: 0,
        };
      current[hazardType] += 1;
      current.total += 1;
      byPlayer.set(playerId, current);
    });

    return Array.from(byPlayer.values())
      .filter((row) => row.total > 0)
      .sort((a, b) => b.total - a.total || a.playerName.localeCompare(b.playerName));
  }, [holeEvents, playerById]);

  const getGameIcon = useCallback((gameName: string) => {
    const normalized = normalizeGameKey(gameName);
    const icon = GAME_ICON_MAP[normalized];
    return icon ?? TrophyOnlyImage;
  }, []);

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#f6d9a6" />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <Text style={styles.error}>{error}</Text>
      </View>
    );
  }

  return (
    <ImageBackground source={HoleIllustration} style={styles.pageBackground} imageStyle={styles.pageBackgroundImage}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.container}>
          <View style={styles.hero}>
            <Text style={styles.title}>{round?.name || t('summary.title')}</Text>
            <Text style={styles.subtitle}>{round?.courseName ?? '-'}</Text>
          </View>

          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>{t('summary.overallTitle')}</Text>
            <View style={styles.scoreboardHeader}>
              <Text style={[styles.headerText, styles.headerPlayer]}>{t('summary.playerHeader')}</Text>
              <Text style={styles.headerText}>{t('summary.pointsHeader')}</Text>
            </View>
            {playerScores.map((player, index) => (
              <View key={player.id} style={[styles.playerRow, index === 0 && styles.leaderRow, styles.playerRowCard]}>
                <Text style={styles.playerName}>{player.name}</Text>
                <Text style={styles.playerScore}>{player.totalPoints}</Text>
              </View>
            ))}
          </View>

          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>{t('summary.gamesTitle')}</Text>
            {gameResultCards.length === 0 ? (
              <Text style={styles.emptyText}>{t('summary.gamesEmpty')}</Text>
            ) : (
              gameResultCards.map((game) => (
                <View key={`${game.gameId}:${game.gameName}`} style={styles.gameCard}>
                  <View style={styles.gameHeaderRow}>
                    <Image source={getGameIcon(game.gameName)} style={styles.gameIcon} resizeMode="cover" />
                    <Text style={styles.gameTitle}>{game.gameName}</Text>
                  </View>
                  {game.rows.map((row) => (
                    <View key={`${game.gameId}:${row.playerId}`} style={styles.gameRow}>
                      <Text style={styles.gameRowPlayer}>{row.playerName}</Text>
                      <Text style={styles.gameRowPoints}>{t('summary.pointsValue', { points: row.points })}</Text>
                    </View>
                  ))}
                </View>
              ))
            )}
          </View>

          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>{t('summary.hazardsTitle')}</Text>
            {hazardByPlayer.length === 0 ? (
              <Text style={styles.emptyText}>{t('summary.hazardsEmpty')}</Text>
            ) : (
              hazardByPlayer.map((row) => (
                <View key={row.playerId} style={styles.hazardRow}>
                  <View style={styles.hazardHeader}>
                    <Text style={styles.hazardPlayer}>{row.playerName}</Text>
                    <View style={styles.hazardBadge}>
                      <Text style={styles.hazardBadgeText}>{t('summary.hazardTotal', { count: row.total })}</Text>
                    </View>
                  </View>
                  <View style={styles.hazardBadges}>
                    {row.water > 0 ? (
                      <View style={styles.hazardBadge}>
                        <Image source={HAZARD_ICON_MAP.water} style={styles.hazardIcon} resizeMode="cover" />
                        <Text style={styles.hazardBadgeText}>{t('summary.hazardWater', { count: row.water })}</Text>
                      </View>
                    ) : null}
                    {row.bunker > 0 ? (
                      <View style={styles.hazardBadge}>
                        <Image source={HAZARD_ICON_MAP.bunker} style={styles.hazardIcon} resizeMode="cover" />
                        <Text style={styles.hazardBadgeText}>{t('summary.hazardBunker', { count: row.bunker })}</Text>
                      </View>
                    ) : null}
                    {row.threePutt > 0 ? (
                      <View style={styles.hazardBadge}>
                        <Image source={HAZARD_ICON_MAP.threePutt} style={styles.hazardIcon} resizeMode="cover" />
                        <Text style={styles.hazardBadgeText}>{t('summary.hazardThreePutt', { count: row.threePutt })}</Text>
                      </View>
                    ) : null}
                    {row.penalty > 0 ? (
                      <View style={styles.hazardBadge}>
                        <Image source={HAZARD_ICON_MAP.penalty} style={styles.hazardIcon} resizeMode="cover" />
                        <Text style={styles.hazardBadgeText}>{t('summary.hazardPenalty', { count: row.penalty })}</Text>
                      </View>
                    ) : null}
                  </View>
                </View>
              ))
            )}
          </View>

          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>{t('summary.mysteryTitle')}</Text>
            {mysteryHighlights.length === 0 ? (
              <Text style={styles.emptyText}>{t('summary.mysteryEmpty')}</Text>
            ) : (
              mysteryHighlights.map((highlight, index) => (
                <View key={`${highlight.holeNumber}:${highlight.mysteryPrize}:${index}`} style={styles.mysteryRow}>
                  <Image source={MysterySelectImage} style={styles.mysteryIcon} resizeMode="cover" />
                  <View style={styles.mysteryTextBlock}>
                    <Text style={styles.mysteryLine}>
                      {t('summary.mysteryHolePrize', {
                        hole: highlight.holeNumber,
                        prize: highlight.mysteryPrize,
                      })}
                    </Text>
                    <Text style={styles.mysterySubgame}>{t('summary.mysterySubgame', { subgame: highlight.subgameName })}</Text>
                    <Text style={styles.mysteryWinner}>{t('summary.mysteryWinner', { winner: highlight.winnerName })}</Text>
                  </View>
                </View>
              ))
            )}
          </View>

          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>{t('summary.parBetterTitle')}</Text>
            {parOrBetterByPlayer.length === 0 ? (
              <Text style={styles.emptyText}>{t('summary.parBetterEmpty')}</Text>
            ) : (
              parOrBetterByPlayer.map((row) => (
                <View key={row.playerName} style={styles.parRow}>
                  <Text style={styles.parRowPlayer}>{row.playerName}</Text>
                  <View style={styles.parRowBadges}>
                    {row.holes.map((entry) => (
                      <View key={`${row.playerName}-${entry.holeNumber}-${entry.resultType}`} style={styles.parBadge}>
                        <Text style={styles.parBadgeText}>{`H${entry.holeNumber} ${entry.resultLabel}`}</Text>
                        {entry.resultType === 'birdie' ? (
                          <Text style={styles.chickenBirdie}>🐔</Text>
                        ) : null}
                      </View>
                    ))}
                  </View>
                </View>
              ))
            )}
          </View>

          <Pressable style={styles.doneButton} onPress={() => router.push('/')}>
            <Text style={styles.doneButtonText}>{t('summary.doneButton')}</Text>
          </Pressable>
        </View>
      </ScrollView>
    </ImageBackground>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#0f182d',
  },
  pageBackground: {
    flex: 1,
  },
  pageBackgroundImage: {
    opacity: 0.16,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'flex-start',
  },
  container: {
    flex: 1,
    padding: 18,
    backgroundColor: 'transparent',
    gap: 12,
  },
  hero: {
    backgroundColor: 'rgba(12, 19, 40, 0.72)',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(246, 217, 166, 0.35)',
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  title: {
    fontSize: 30,
    fontWeight: '900',
    color: '#f6d9a6',
    textAlign: 'left',
  },
  subtitle: {
    fontSize: 14,
    color: '#d9e5ff',
    textAlign: 'left',
    marginTop: 4,
  },
  sectionCard: {
    backgroundColor: 'rgba(12, 19, 40, 0.78)',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(164, 187, 235, 0.26)',
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 8,
  },
  sectionTitle: {
    color: '#f2f6ff',
    fontSize: 17,
    fontWeight: '800',
  },
  scoreboardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(168, 186, 226, 0.28)',
    paddingBottom: 6,
  },
  headerText: {
    color: '#d9e5ff',
    fontWeight: '800',
    fontSize: 13,
  },
  headerPlayer: {
    flex: 1,
  },
  playerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  playerRowCard: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(164, 187, 235, 0.2)',
  },
  leaderRow: {
    backgroundColor: 'rgba(246, 217, 166, 0.18)',
    borderColor: 'rgba(246, 217, 166, 0.58)',
  },
  playerName: {
    color: '#eef3ff',
    fontSize: 15,
    flex: 1,
  },
  playerScore: {
    color: '#f6d9a6',
    fontSize: 17,
    fontWeight: '900',
  },
  gameCard: {
    backgroundColor: 'rgba(7, 12, 26, 0.72)',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(164, 187, 235, 0.18)',
    padding: 10,
    gap: 6,
  },
  gameHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  gameIcon: {
    width: 30,
    height: 30,
    borderRadius: 6,
  },
  gameTitle: {
    color: '#f2f6ff',
    fontSize: 15,
    fontWeight: '800',
  },
  gameRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 3,
  },
  gameRowPlayer: {
    color: '#dbe7ff',
    fontSize: 14,
    flex: 1,
  },
  gameRowPoints: {
    color: '#f6d9a6',
    fontSize: 14,
    fontWeight: '800',
  },
  hazardRow: {
    gap: 6,
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(164, 187, 235, 0.18)',
  },
  hazardPlayer: {
    color: '#f2f6ff',
    fontSize: 14,
    fontWeight: '800',
  },
  hazardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
  },
  hazardBadges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  hazardBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(164, 187, 235, 0.24)',
    backgroundColor: 'rgba(12, 19, 40, 0.65)',
  },
  hazardBadgeText: {
    color: '#dbe7ff',
    fontSize: 12,
    fontWeight: '700',
  },
  hazardIcon: {
    width: 25,
    height: 25,
    borderRadius: 4,
  },
  mysteryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderWidth: 1,
    borderColor: 'rgba(164, 187, 235, 0.18)',
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: 'rgba(7, 12, 26, 0.65)',
  },
  mysteryIcon: {
    width: 28,
    height: 28,
    borderRadius: 6,
  },
  mysteryTextBlock: {
    flex: 1,
    gap: 2,
  },
  mysteryLine: {
    color: '#e8efff',
    fontSize: 13,
    fontWeight: '700',
  },
  mysteryWinner: {
    color: '#f6d9a6',
    fontSize: 13,
    fontWeight: '800',
  },
  mysterySubgame: {
    color: '#dbe7ff',
    fontSize: 12,
    fontWeight: '700',
  },
  parRow: {
    borderWidth: 1,
    borderColor: 'rgba(164, 187, 235, 0.18)',
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: 'rgba(7, 12, 26, 0.65)',
    gap: 4,
  },
  parRowPlayer: {
    color: '#f2f6ff',
    fontSize: 14,
    fontWeight: '800',
  },
  parRowBadges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  parBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(164, 187, 235, 0.24)',
    backgroundColor: 'rgba(12, 19, 40, 0.65)',
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  parBadgeText: {
    color: '#dbe7ff',
    fontSize: 12,
    fontWeight: '700',
  },
  chickenBirdie: {
    fontSize: 14,
    lineHeight: 14,
  },
  emptyText: {
    color: '#a9bddf',
    fontSize: 13,
  },
  doneButton: {
    backgroundColor: '#34d399',
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
    marginTop: 4,
  },
  doneButtonText: {
    color: '#1b2033',
    fontWeight: '900',
    fontSize: 16,
  },
  error: {
    color: '#ff7a7a',
    textAlign: 'center',
  },
});
